import type { PdfTemplateDesign, PdfTemplateCompany } from "@workspace/api-zod/pdf-template";
import type { PdfTemplateProfile } from "@workspace/api-client-react";
import { generateQuotePDF, type QuoteData, type CompanyInfo } from "@/lib/pdf-generator";

export const HEX = /^#[0-9a-f]{6}$/i;
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;

export function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as { data?: { message?: unknown } | null; message?: unknown } | null;
  if (e?.data && typeof e.data.message === "string" && e.data.message) return e.data.message;
  if (typeof e?.message === "string" && e.message) return e.message.replace(/^HTTP \d+[^:]*:\s*/, "");
  return fallback;
}

export function designsEqual(a: PdfTemplateDesign | null, b: PdfTemplateDesign | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function designProblems(d: PdfTemplateDesign): string[] {
  const p: string[] = [];
  if (!HEX.test(d.primaryColor)) p.push("Main colour must be a six-digit hex code like #374349.");
  if (!HEX.test(d.accentColor)) p.push("Accent colour must be a six-digit hex code like #5aabbf.");
  if (!d.coverTitle.trim()) p.push("Cover title cannot be empty.");
  if (!d.thankYouTitle.trim()) p.push("Thank-you title cannot be empty.");
  return p;
}

export function snapshotCompany(profile: PdfTemplateProfile, logoUrl: string | null): PdfTemplateCompany {
  return {
    name: profile.companyName || "Your company",
    address: profile.address || undefined,
    email: profile.email || undefined,
    phone: profile.phone || undefined,
    website: profile.website || undefined,
    vatNumber: profile.vatNumber || undefined,
    logoUrl: logoUrl ?? undefined,
    showCompanyName: profile.showCompanyName ?? true,
    logoSize: profile.logoSize ?? "medium",
  };
}

/** Synthetic sample job. Never saved, never tied to a real customer. */
export function buildSampleQuote(design: PdfTemplateDesign, company: PdfTemplateCompany): QuoteData {
  const now = new Date();
  const valid = new Date(now.getTime() + 30 * 86400000);
  const item = (category: string, name: string, description: string, quantity: string, unit: string, unitCost: string) => ({
    category, name, description, quantity, unit, unitCost,
    subtotal: (Number(quantity) * Number(unitCost)).toFixed(2),
    labourCost: (Number(quantity) * Number(unitCost) * 0.55).toFixed(2),
    materialCost: (Number(quantity) * Number(unitCost) * 0.4).toFixed(2),
    plantCost: (Number(quantity) * Number(unitCost) * 0.05).toFixed(2),
  });
  const lineItems = [
    item("preliminaries", "Site set-up and protection", "Dust sheets, floor protection and welfare for the duration of works.", "1", "item", "420.00"),
    item("groundworks", "Strip foundations", "Excavate and pour concrete strip foundations to engineer's depth.", "9.6", "m3", "185.50"),
    item("brickwork", "Cavity wall build", "Facing brick outer leaf to match existing, block inner leaf.", "38.4", "m2", "142.75"),
    item("roofing", "Flat roof covering", "Warm-deck EPDM system with new fascia and trims.", "21.3", "m2", "96.40"),
    item("electrics", "First and second fix electrics", "Six double sockets, four downlights and a fused spur.", "1", "item", "1180.00"),
    item("decorating", "Mist coat and two finish coats", "Walls and ceilings throughout the new extension.", "64", "m2", "11.80"),
  ];
  const subtotal = lineItems.reduce((s, i) => s + Number(i.subtotal), 0);
  const overhead = subtotal * 0.1, profit = subtotal * 0.15, contingency = subtotal * 0.05;
  const net = subtotal + overhead + profit + contingency, vat = net * 0.2;
  return {
    quoteNumber: "SAMPLE-0427",
    status: "draft",
    projectAddress: "14 Larkspur Close, Harrowgate Hill",
    projectType: "single_storey_extension",
    description: "Single-storey rear extension with flat roof. This is a sample quote used only to preview your design.",
    subtotal: subtotal.toFixed(2),
    overheadAmount: overhead.toFixed(2),
    profitAmount: profit.toFixed(2),
    contingencyAmount: contingency.toFixed(2),
    totalPrice: net.toFixed(2),
    vatRate: "20",
    vatAmount: vat.toFixed(2),
    grandTotal: (net + vat).toFixed(2),
    includeVat: true,
    validUntil: valid,
    assumptions: "Clear access to the rear garden. Building control fees paid by the client.",
    exclusions: "Kitchen units, flooring finishes and structural engineer fees.",
    createdAt: now,
    lineItems,
    client: { name: "Sample Customer", address: "14 Larkspur Close", postcode: "SAMPLE" },
    pdfTemplate: { version: "preview", design, company },
  };
}

export async function renderPreviewPdf(
  design: PdfTemplateDesign,
  profile: PdfTemplateProfile,
  onWarning: (message: string) => void,
): Promise<Blob> {
  const company = { ...snapshotCompany(profile, design.logoUrl),
    showCompanyName: design.showCompanyName ?? profile.showCompanyName ?? true };
  const info: CompanyInfo = { ...company, market: profile.market } as CompanyInfo;
  const out = await generateQuotePDF(buildSampleQuote(design, company), info, { returnBase64: true, onWarning });
  if (typeof out !== "string" || !out) throw new Error("The preview could not be generated.");
  const b64 = out.includes("base64,") ? out.slice(out.indexOf("base64,") + 7) : out;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: "application/pdf" });
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not read that file."));
    r.readAsDataURL(file);
  });
}
